import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faRetweet } from '@fortawesome/pro-regular-svg-icons';
import { faBolt } from '@fortawesome/pro-solid-svg-icons';

import Price from '@v2/shared/components/Price';
import Currency from '@v2/shared/components/Currency';
import { cn } from '@v2/shared/lib/utils';
import { useSite } from '@v2/shared/lib/site';
import { PRODUCT_TYPE_MAGAZINE, PRODUCT_TYPE_POSTCARD, SaleProduct } from '@v2/shared/lib/megaSale';

import SaleMsrp from './SaleMsrp';
import ProductForm from './ProductForm';
import useProductTile, { PRICE_COLOR } from './useProductTile';
import ProductDetailDialog from './ProductDetailDialog';

/**
 * The cover's box, as one declaration the skeleton can copy.
 *
 * It is responsive because a fixed 180px column is what broke the button. On a 390px
 * phone the cover, the gaps and the padding left 115px for everything else, and "Add to
 * Cart" wrapped onto three lines inside it — no amount of shrinking the button alone
 * fixes a column that narrow, so the cover gives the width back instead.
 *
 * Exported so ProductSkeleton states the same numbers. They have to agree exactly: the
 * skeleton stands in for the tile while it is out of view, and a skeleton of a different
 * height moves the page under the reader as they scroll past it.
 */
export const COVER_WIDTH = 'w-[120px] sm:w-[150px] xl:w-[180px]';
export const COVER_HEIGHT = 'h-[155px] sm:h-[193px] xl:h-[232px]';

/**
 * A postcard's box, in place of the cover's.
 *
 * The cover's box is a magazine's shape, and a landscape card drawn across its width is
 * about half its height, which left 37-55px of white above and below the card while the
 * name and the price sat at the top of the tile. A postcard has no Term or Type to choose,
 * so the column beside it is only the name, the price and the button: 45px for a name on
 * two lines, which is what most take in these columns, 32px for the price and 56px for the
 * button with its margin. The box is that column's height, and the card, 82-122px across
 * the breakpoints, sits in the middle of it.
 *
 * One height at every width, because the column does not change with the width — only the
 * card does. A name long enough for a third line makes its row taller, as it would a
 * magazine's. Exported, like the cover's box, for ProductSkeleton.
 */
export const POSTCARD_HEIGHT = 'h-[133px]';

export type ProductCardProps = {
  product: SaleProduct;
  categoryId: number;
  highestTerm: boolean;
  showCoverPrice: boolean;
  /** Open the detail dialog — the deep-linked product, or one chosen from the search. */
  openDetail?: boolean;
  /**
   * Which request asked for it.
   *
   * `openDetail` alone cannot express "again". A customer who chooses the same magazine
   * from the search a second time is asking for the same product with the same flag, so
   * without this the prop never changes and the dialog they closed a moment ago stays
   * closed.
   */
  detailRequest?: number;
  /**
   * The request has been answered — the customer has closed the dialog.
   *
   * Required rather than optional because the tile is unmounted whenever it scrolls out
   * of the render window, so a caller that forgets to act on this hands the flag back to
   * the tile the next time it mounts and the dialog opens itself. That is a bug worth a
   * compile error rather than a silent reappearance.
   */
  onDetailClose: () => void;
  onAdd: (product: SaleProduct, skuCode: string, giftSkuCode: string | null, offerCode: string | null) => Promise<void>;
};

/**
 * One product on the sale.
 *
 * The id is `prod_<categoryId>_<productId>` because the same title can appear in more
 * than one of a sale's categories, so the product id alone does not name an element —
 * which is also why deep links carry both.
 */
export default function ProductCard({
  product,
  categoryId,
  highestTerm,
  showCoverPrice,
  openDetail = false,
  detailRequest,
  onDetailClose,
  onAdd,
}: ProductCardProps) {
  const site = useSite();
  const tile = useProductTile(product, highestTerm);
  const [detailOpen, setDetailOpen] = useState(false);
  const [showBack, setShowBack] = useState(false);

  /*
   * Opened by the request rather than by the prop's initial value.
   *
   * It was `useState(openDetail)`, which reads the flag once, when the tile mounts. That
   * happened to work for a deep link — the linked tile is far down the page, so it mounts
   * with the flag already true — and never worked for the search, because the tile the
   * customer picked was usually already on screen and already mounted. The flag went from
   * false to true and the initial state had long since been decided.
   */
  useEffect(() => {
    if (openDetail) {
      setDetailOpen(true);
    }
  }, [openDetail, detailRequest]);

  /*
   * Closing answers the request, and says so.
   *
   * Reacting to the flag on every mount rather than only on the first is what made the
   * search reliable, and it is also what makes an unanswered request dangerous: this tile
   * is unmounted as soon as it scrolls far enough away and mounted again on the way back,
   * so a flag left true reopens a dialog the customer closed a scroll ago. The state lives
   * above the tile — it has to, since the tile does not survive the journey — so the tile
   * cannot forget the request by itself and has to report it spent.
   */
  const closeDetail = () => {
    setDetailOpen(false);
    onDetailClose();
  };

  const selected = tile.product;
  const isMagazine = selected.product_type === PRODUCT_TYPE_MAGAZINE;
  const isPostcard = selected.product_type === PRODUCT_TYPE_POSTCARD;

  /*
   * A postcard's tile fills its cell of the grid, and its card is centred on whatever height
   * that turns out to be. A name that takes a third line makes its row taller than
   * POSTCARD_HEIGHT allows for, and without this the other tiles in the row kept their own
   * height at the top of it — their buttons 22px above its button, its card 11px above
   * its middle. With Avenir Next, the site's font, "Merry Christmas & Happy New Year
   * Postcard" does that at most widths.
   */
  return (
    <div id={`prod_${categoryId}_${product.id}`} className={cn('relative p-3', isPostcard && 'h-full')}>
      <div className={cn('flex gap-4', isPostcard && 'h-full')}>
        <div className={cn(COVER_WIDTH, 'shrink-0 relative', isPostcard && 'self-center')}>
          {isPostcard ? (
            /*
             * The card fills the box's width at its own height, centred in a box of
             * POSTCARD_HEIGHT rather than the cover's — see there for why. The swap button
             * is placed on the card rather than on the box, where it floated in the empty
             * band under the card.
             */
            <div className={cn(POSTCARD_HEIGHT, 'flex items-center')}>
              <div className="relative w-full">
                <img
                  src={showBack ? '/resources/main/images/postcard-back-sample4.png' : selected.images.normal}
                  srcSet={showBack ? undefined : `${selected.images.retina} 2x`}
                  width={720}
                  height={488}
                  alt={selected.name}
                  loading="lazy"
                  className="w-full h-auto"
                />
                <button
                  type="button"
                  className="absolute bottom-1 right-1 text-slate-500 hover:text-slate-800 bg-white/90 rounded p-1"
                  onClick={() => setShowBack((back) => !back)}
                  aria-label={showBack ? 'View the front of this postcard' : 'View the back of this postcard'}
                >
                  <FontAwesomeIcon icon={faRetweet} />
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setDetailOpen(true)}
              className="block"
              aria-label={`More about ${selected.name}`}
            >
              <img
                src={selected.images.normal}
                srcSet={`${selected.images.retina} 2x`}
                width={180}
                height={232}
                alt={selected.name}
                loading="lazy"
                className={cn(COVER_WIDTH, COVER_HEIGHT, 'object-contain drop-shadow')}
              />
            </button>
          )}
          {selected.is_digital && (
            <span className="absolute top-1 left-1 inline-flex items-center gap-1 rounded bg-slate-900/80 px-1.5 py-0.5 text-[11px] font-semibold text-white">
              <FontAwesomeIcon icon={faBolt} />
              Digital
            </span>
          )}
        </div>

        <div className="flex-grow min-w-0 flex flex-col">
          <h3 className="m-0 text-base font-semibold text-slate-900 leading-snug">
            {selected.name}{' '}
            <button
              type="button"
              onClick={() => setDetailOpen(true)}
              className="text-slate-400 hover:text-primary-600 align-middle"
              aria-label={`More about ${selected.name}`}
            >
              <FontAwesomeIcon icon={faInfoCircle} className="text-sm" />
            </button>
          </h3>

          <p className="m-0 mt-1 flex flex-wrap items-baseline gap-x-2">
            <Price value={tile.price} className={cn('text-xl', PRICE_COLOR)} />
            {isMagazine && tile.perIssue !== null && (
              <span className="text-sm text-primary-500">
                (<Currency value={tile.perIssue} currencyCode={site.currency} />
                /issue)
              </span>
            )}
          </p>
          <p className="m-0">
            <SaleMsrp
              product={selected}
              price={tile.price}
              pricePercentage={tile.selectedTerm ? parseFloat(String(tile.selectedTerm.percent ?? 0)) || 0 : 0}
              showCoverPrice={showCoverPrice}
            />
          </p>

          {/*
            No description here. The legacy landing page never showed one — it is a
            detail, and it lives in the (i) view, which ProductDetailDialog still draws.
            Three lines of copy per tile also cost the grid the column this round adds.
          */}

          <div className="mt-auto">
            <ProductForm
              tile={tile}
              product={selected}
              onAdd={(skuCode, offerCode) => onAdd(product, skuCode, tile.giftSkuCode, offerCode)}
            />
          </div>
        </div>
      </div>

      <ProductDetailDialog
        isOpen={detailOpen}
        close={closeDetail}
        product={product}
        highestTerm={highestTerm}
        showCoverPrice={showCoverPrice}
        onAdd={(skuCode, giftSkuCode, offerCode) => onAdd(product, skuCode, giftSkuCode, offerCode)}
      />
    </div>
  );
}
